// ============================================================================
// useSlotAvailability — свободные окна выбранного специалиста на день.
// Занятость — серверные записи (loadDoctorAppointments), сетка — настройка
// пользователя (buildDaySlots). Один механизм для страницы и диалога записи.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { friendlyError } from '@/lib/errors';
import {
  buildDaySlots,
  loadDoctorAppointments,
  type AppointmentCapabilities,
  type AppointmentItem,
  type AppointmentScheduleSettings,
  type AppointmentSlot,
  type AppointmentSource,
} from '@/lib/medicAppointments';

export interface UseSlotAvailabilityInput {
  capabilities: AppointmentCapabilities;
  source: AppointmentSource;
  doctorId: string;
  dateKey: string;
  durationMin: number;
  settings: AppointmentScheduleSettings;
  /** Запись, которую переносим: её интервал не должен считаться занятым. */
  excludeKey?: string;
  refreshKey?: number;
  /** Загружать занятость только когда экран/диалог открыт. */
  enabled?: boolean;
}

export interface SlotAvailability {
  slots: AppointmentSlot[];
  closed: boolean;
  loading: boolean;
  error: string;
  reload: () => void;
}

export function useSlotAvailability(input: UseSlotAvailabilityInput): SlotAvailability {
  const {
    capabilities, source, doctorId, dateKey, durationMin, settings, excludeKey, refreshKey = 0, enabled = true,
  } = input;
  const [items, setItems] = useState<AppointmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const lastKeyRef = useRef('');

  const reload = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    const key = `${source}:${doctorId}`;
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      setItems([]);
      setError('');
    }
    if (!enabled || !doctorId || !capabilities[source].available) {
      setItems([]);
      setLoading(false);
      setError('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    loadDoctorAppointments(doctorId, capabilities, false)
      .then((loaded) => {
        if (cancelled) return;
        setItems(loaded.items);
        setError(loaded.state === 'ready' ? '' : (loaded.error || 'Не удалось загрузить занятость специалиста'));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setItems([]);
        setError(friendlyError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [capabilities, source, doctorId, refreshKey, reloadKey, enabled]);

  const result = useMemo(() => {
    if (!doctorId) return { slots: [] as AppointmentSlot[], closed: false };
    const busy = excludeKey ? items.filter((item) => item.key !== excludeKey) : items;
    return buildDaySlots(dateKey, source, durationMin, busy, settings);
  }, [items, source, dateKey, durationMin, settings, excludeKey, doctorId]);

  const stale = loading && items.length === 0;
  return {
    slots: stale ? [] : result.slots,
    closed: stale ? false : result.closed,
    loading: stale,
    error,
    reload,
  };
}
